/**
 * Helpers for matching grants by Category field (comma-separated values).
 */

const normalizeCategory = (category) =>
  String(category || '')
    .toLowerCase()
    .trim();

/**
 * Returns true if the grant's Category includes any of the target categories.
 * Supports comma-separated values, e.g. "Hiring, R&D".
 */
export function grantMatchesCategory(grant, categories) {
  const grantCategory = normalizeCategory(grant?.fields?.Category);
  if (!grantCategory) return false;

  const targets = (Array.isArray(categories) ? categories : [categories]).map(normalizeCategory);

  return targets.some((target) => {
    if (!target) return false;
    return grantCategory.includes(target);
  });
}

export function isMitacsGrant(grant) {
  const name = grant?.fields?.Name || '';
  return /mitacs/i.test(name);
}

export function isHiringOrRdGrant(grant) {
  return (
    grantMatchesCategory(grant, 'hiring') ||
    grantMatchesCategory(grant, 'r&d') ||
    isMitacsGrant(grant)
  );
}

export function getGrantCategoryLabel(grant) {
  if (isMitacsGrant(grant)) return 'Mitacs';
  if (grantMatchesCategory(grant, 'hiring')) return 'Hiring';
  if (grantMatchesCategory(grant, 'r&d')) return 'R&D';
  return grant?.fields?.Category || 'Other';
}

const US_STATES = new Set([
  'alabama', 'alaska', 'arizona', 'arkansas', 'california', 'colorado',
  'connecticut', 'delaware', 'florida', 'georgia', 'hawaii', 'idaho',
  'illinois', 'indiana', 'iowa', 'kansas', 'kentucky', 'louisiana',
  'maine', 'maryland', 'massachusetts', 'michigan', 'minnesota',
  'mississippi', 'missouri', 'montana', 'nebraska', 'nevada',
  'new hampshire', 'new jersey', 'new mexico', 'new york',
  'north carolina', 'north dakota', 'ohio', 'oklahoma', 'oregon',
  'pennsylvania', 'rhode island', 'south carolina', 'south dakota',
  'tennessee', 'texas', 'utah', 'vermont', 'virginia', 'washington',
  'west virginia', 'wisconsin', 'wyoming',
]);

/** Parse Eligible_Region into normalized lowercase region tokens. */
export function parseGrantRegionList(grant) {
  const raw = grant?.fields?.Eligible_Region;
  if (!raw) return [];
  const str = Array.isArray(raw) ? raw.join(',') : String(raw);
  return str
    .toLowerCase()
    .split(',')
    .map((region) => region.trim())
    .filter(Boolean);
}

/**
 * True when a grant is US-only (United States or US states, no Canada/provinces).
 * Matches AllGrants default featured-grant exclusion logic.
 */
export function isUnitedStatesOnlyGrant(grant) {
  const regions = parseGrantRegionList(grant);
  if (!regions.length) return false;

  if (regions.length === 1 && regions[0] === 'united states') {
    return true;
  }

  return regions.every(
    (region) => region === 'united states' || US_STATES.has(region)
  );
}

/** Sort rank: hiring & Mitacs first, then R&D-only, then other. Lower is higher priority. */
export function getGrantCategorySortRank(grant) {
  const hiring = grantMatchesCategory(grant, 'hiring') || isMitacsGrant(grant);
  const rd = grantMatchesCategory(grant, 'r&d');
  if (hiring) return 0;
  if (rd) return 1;
  return 2;
}
